import { Opt } from "@/components/Common";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { InventoryTable } from "@/components/AdminInventory";
import { STATUS } from "@/pages/MyBookings";
import { Download } from "lucide-react";
import { downloadPdf, errMsg, http, inr } from "@/lib/api";

const TABS = ["bookings", "coupons", "packages", "hotels", "flights", "cabs", "enquiries"];

const Stats = () => {
  const { data } = useQuery({ queryKey: ["admin", "stats"], queryFn: () => http.get("/admin/stats").then((r) => r.data) });
  if (!data) return null;
  const items = [["Revenue (paid)", inr(data.revenue)], ["Bookings", data.bookings], ["Awaiting payment", data.pending], ["Confirmed", data.confirmed], ["Users", data.users], ["Enquiries", data.enquiries]];
  return (
    <div className="grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-[#E5E0D8] bg-[#E5E0D8] md:grid-cols-6">
      {items.map(([k, v]) => (
        <div key={k} className="bg-white p-5" data-testid={`admin-stat-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
          <p className="text-xs uppercase tracking-wider text-slate-500">{k}</p>
          <p className="font-display mt-1 text-3xl font-bold text-[#0D1B2A]">{v}</p>
        </div>
      ))}
    </div>
  );
};

const Bookings = () => {
  const qc = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ["admin", "bookings"], queryFn: () => http.get("/admin/bookings").then((r) => r.data) });
  const setStatus = async (b, status) => {
    await http.patch(`/admin/bookings/${b.id}`, { status });
    toast.success(`${b.ref} → ${STATUS[status][0]}`);
    qc.invalidateQueries({ queryKey: ["admin"] });
  };
  if (!data.length) return <p className="text-slate-500" data-testid="admin-bookings-empty">No bookings yet.</p>;
  return (
    <div className="overflow-x-auto rounded-xl border border-[#E5E0D8] bg-white" data-testid="admin-bookings-table">
      <table className="w-full text-sm">
        <thead className="bg-[#F4F0EA] text-left text-xs uppercase tracking-wider text-slate-500">
          <tr>{["Ref", "Type", "Booking", "Customer", "Date", "Amount", "Status"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr>
        </thead>
        <tbody>
          {data.map((b) => (
            <tr key={b.id} className="border-t border-[#E5E0D8]" data-testid={`admin-booking-${b.ref}`}>
              <td className="px-4 py-3 font-mono text-xs">{b.ref}</td>
              <td className="px-4 py-3 capitalize">{b.type}</td>
              <td className="px-4 py-3 max-w-xs">{b.title}</td>
              <td className="px-4 py-3">{b.contact_name}<br /><span className="text-xs text-slate-500">{b.contact_phone} · {b.contact_email}</span></td>
              <td className="px-4 py-3 whitespace-nowrap">{b.travel_date}</td>
              <td className="px-4 py-3 font-semibold">{inr(b.amount)}{b.coupon_code && <span className="block text-xs font-normal text-emerald-700">{b.coupon_code} −{inr(b.discount)}</span>}</td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                <select value={b.status} onChange={(e) => setStatus(b, e.target.value)} className="rounded-md border border-[#E5E0D8] px-2 py-1 text-xs" data-testid={`admin-status-${b.ref}`}>
                  {Object.entries(STATUS).map(([k, [l]]) => <Opt key={k} v={k} l={l} />)}
                </select>
                <button onClick={() => downloadPdf(b).catch((e) => toast.error(errMsg(e)))} title="Download PDF" className="text-slate-500 hover:text-[#0D1B2A]" data-testid={`admin-pdf-${b.ref}`}><Download className="h-4 w-4" /></button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const Enquiries = () => {
  const { data = [] } = useQuery({ queryKey: ["admin", "enquiries"], queryFn: () => http.get("/admin/enquiries").then((r) => r.data) });
  if (!data.length) return <p className="text-slate-500">No enquiries yet.</p>;
  return (
    <div className="space-y-3" data-testid="admin-enquiries">
      {data.map((e) => (
        <div key={e.id} className="rounded-xl border border-[#E5E0D8] bg-white p-4 text-sm">
          <p className="font-semibold">{e.name} <span className="font-normal text-slate-500">· {e.email} {e.phone && `· ${e.phone}`}</span></p>
          <p className="mt-1 text-slate-700">{e.message}</p>
        </div>
      ))}
    </div>
  );
};

export default function Admin() {
  const [tab, setTab] = useState("bookings");
  return (
    <div className="mx-auto max-w-7xl px-4 py-12 sm:px-8" data-testid="admin-page">
      <p className="eyebrow">Admin</p>
      <h1 className="font-display mt-2 mb-8 text-4xl font-bold text-[#0D1B2A] sm:text-5xl">Control panel</h1>
      <Stats />
      <div className="mt-10 mb-6 flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`}
            className={`rounded-full px-4 py-2 text-sm font-semibold capitalize transition-colors ${tab === t ? "bg-[#0D1B2A] text-white" : "bg-white border border-[#E5E0D8] text-slate-600"}`}>{t}</button>
        ))}
      </div>
      {tab === "bookings" && <Bookings />}
      {tab === "enquiries" && <Enquiries />}
      {["coupons", "packages", "hotels", "flights", "cabs"].includes(tab) && <InventoryTable key={tab} kind={tab} />}
    </div>
  );
}
